import { useEffect, useState } from "react"
import Modal from "Components/molecule/modal"
import LoadingState from "Components/organisms/loading-state"
import { KpiDetail, KpiDetailColumn, KpiRow } from "models/financial-kpi"
import { getFinancialKpiDetail } from "slices/finance/financial-kpi/thunk"
import { formatDate } from "utils/tools"
import { FISCAL_MONTHS, formatKpiValue } from "./format"
import { KpiCellPeriod } from "./kpi-master-table"

export interface KpiDetailClick {
    row: KpiRow
    period: KpiCellPeriod
    value: number | null
}

interface KpiDetailModalProps {
    fiscalYear: number
    click: KpiDetailClick | null
    onClose: () => void
}

const formatCell = (value: unknown, column: KpiDetailColumn): string => {
    if (value === null || value === undefined || value === "") return "—"
    switch (column.type) {
        case "currency": return formatKpiValue(Number(value), "currency")
        case "percent": return formatKpiValue(Number(value), "percent")
        case "number": return Number(value).toLocaleString("en-CA", { maximumFractionDigits: 1 })
        case "date": return formatDate(String(value))
        default: return String(value)
    }
}

const isNumeric = (column: KpiDetailColumn) => column.type !== "text" && column.type !== "date"

// Financial KPI Metrics: what makes up one month / YTD value (projects, opportunities, employees, ...).
const KpiDetailModal = ({ fiscalYear, click, onClose }: KpiDetailModalProps) => {
    const [detail, setDetail] = useState<KpiDetail | null>(null)
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        setDetail(null)
        if (!click) return
        setIsLoading(true)
        getFinancialKpiDetail(fiscalYear, click.row.key, click.period).then(data => {
            setDetail(data)
            setIsLoading(false)
        })
    }, [click, fiscalYear])

    if (!click) return null

    const periodLabel = click.period === "ytd" ? `FY${fiscalYear} YTD` : `${FISCAL_MONTHS[click.period - 1]} FY${fiscalYear}`

    return (
        <Modal
            isOpen={!!click}
            onClose={onClose}
            title={`${click.row.label} — ${periodLabel}`}
            subtitle={formatKpiValue(click.value, click.row.unit)}
            className="fit-content"
        >
            <div style={{ minWidth: 480, maxWidth: "85vw" }}>
                {isLoading && <LoadingState title="Loading" subtitle="Fetching the rows behind this value…" />}
                {!isLoading && detail && <>
                    {detail.note && <p className="text-muted mb-2">{detail.note}</p>}
                    {detail.rows.length === 0
                        ? detail.columns.length > 0 && <p className="mb-0">No rows for this value.</p>
                        : <div style={{ maxHeight: "60vh", overflow: "auto" }}>
                            <table className="data-table table align-middle mb-0 fkpi-detail-table">
                                <thead>
                                    <tr>{detail.columns.map(c => <th key={c.key} className={isNumeric(c) ? "text-end" : ""}>{c.label}</th>)}</tr>
                                </thead>
                                <tbody>
                                    {detail.rows.map((r, i) => (
                                        <tr key={i}>
                                            {detail.columns.map(c => (
                                                <td key={c.key} className={isNumeric(c) ? "text-end text-nowrap" : ""}>{formatCell(r[c.key], c)}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                                {detail.total &&
                                    <tfoot>
                                        <tr>
                                            {detail.columns.map(c => (
                                                <td key={c.key} className={isNumeric(c) ? "text-end text-nowrap" : ""}>
                                                    {detail.total![c.key] === undefined ? "" : formatCell(detail.total![c.key], c)}
                                                </td>
                                            ))}
                                        </tr>
                                    </tfoot>}
                            </table>
                        </div>}
                </>}
            </div>
        </Modal>
    )
}

export default KpiDetailModal
